import React, { useState, useRef, useEffect } from 'react';
import { scoreColor } from '../utils/breakScore';
import { formatCurrency } from '../utils/formatters';

// Inline cell that shows the Break Score at the same size as surrounding
// stats. Opens a popover with a larger numeric and the score breakdown
// (margin, revenue, projected sales, margin pct) on hover/focus, and on tap.
//
// The tap handler matters: hover and focus alone left the breakdown
// unreachable on a touch device, because iOS does not reliably focus a
// tabIndex div when you tap it.
//
// Used by the editable Break Spots page (Breaks.jsx) and the view-only
// BreakDetailPanel so both render the score identically.
export default function ScoreCell({
  score,
  label = 'Projected Score',
  breakdown,
  fontSize = '18px',
  showBreakdown = true,
}) {
  const [show, setShow] = useState(false);
  const wrapRef = useRef(null);
  const color = score != null ? scoreColor(score) : 'var(--text-light)';

  // Dismiss on an outside tap/click, mirroring KPICard's tooltip.
  useEffect(() => {
    if (!show) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [show]);

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', display: 'inline-block', cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
      onClick={(e) => { e.stopPropagation(); setShow(s => !s); }}
      tabIndex={0}
    >
      <div style={{ fontSize, fontWeight: 700, lineHeight: 1.1, color }}>
        {score != null ? score : '--'}
      </div>
      {show && (
        <div
          role="tooltip"
          className="score-popover"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: 'white',
            border: '1px solid var(--border, #ddd)',
            padding: '10px 12px',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            zIndex: 1000,
            minWidth: 220,
            whiteSpace: 'nowrap',
            color: 'var(--text, #111)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color }}>
              {score != null ? score : '--'}
            </span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-light)' }}>
              {label}
            </span>
          </div>
          {showBreakdown && breakdown && (
            <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.6, color: 'var(--text-light)' }}>
              <div>Margin Score: {breakdown.marginScore} / 85</div>
              <div>Revenue Score: {breakdown.revenueScore} / 15</div>
              {breakdown.projectedSales != null && (
                <div>Projected Sales: {formatCurrency(breakdown.projectedSales)}</div>
              )}
              {breakdown.marginPct != null && (
                <div>Margin: {Number(breakdown.marginPct).toFixed(1)}%</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
